import { useEffect, useRef, useState } from "react";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Layout from "../components/layout/Layout";
import http from "../config";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Spinner from "../components/common/Spinner";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SignUp = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isValidForm, setIsValidForm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const emailRef = useRef();
  const token = localStorage.getItem("token");

  const navigate = useNavigate();

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const modifiedData = {
      ...formData,
      email: formData.email.trim().toLowerCase(),
    };

    try {
      setIsSending(true);

      const response = await http.post("/sign-up", modifiedData);

      if (response.data.success || response.status === 201) {
        setSuccessMessage(true);
        setFormData({
          email: "",
          password: "",
        });

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/auth");
        }, 2000);
      }
    } catch (error) {
      if (error.status === 400) {
        setError("Email already exists or invalid data");
      } else if (error.status === 409) {
        setError("User already exists with this email");
      } else {
        setError("Error creating user. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  };

  const isValidEmail = (email) => {
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const { email, password } = formData;
    if (name === "email") {
      setIsValidForm(isValidEmail(value.trim()) && password && password.length >= 6);
    } else {
      setIsValidForm(isValidEmail(email.trim()) && value && value.length >= 6);
    }

    setError(null);
    setFormData((prev) => {
      return { ...prev, [name]: value };
    });
  };

  useEffect(() => {
    if (token) {
      navigate("/leads");
    }

    emailRef.current?.focus();
  }, [token, navigate]);

  return (
    <Layout title="Sign Up">
      <div className="min-h-screen flex justify-center items-center px-4">
        <form className="w-full max-w-[500px]" onSubmit={handleFormSubmit}>
          <h2 className="mb-8 text-center">Create Account</h2>

          {successMessage && (
            <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
              <Check className="text-green-600" size={20} />
              <div>
                <p className="b3 text-green-700 font-semibold">
                  New user created successfully!
                </p>
                <p className="b4 text-green-600">
                  Redirecting to login...
                </p>
              </div>
            </div>
          )}

          {error && <p className="b3 text-red-500 mb-3">{error}</p>}

          {!successMessage && (
            <>
              <Input
                name="email"
                placeholder="Enter your email"
                styles="mb-2"
                value={formData.email}
                onChange={handleInputChange}
                ref={emailRef}
              />
              <Input
                type="password"
                name="password"
                placeholder="Enter your password (minimum 6 characters)"
                styles="mb-6"
                value={formData.password}
                onChange={handleInputChange}
              />
              <Button
                text={isSending ? "Creating..." : "Create Account"}
                disabled={!isValidForm || isSending}
                Icon={isSending ? Spinner : ArrowRight}
              />
              <div className="mt-4 text-center">
                <p className="b3 text-grey-05">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/auth")}
                    className="text-purple-06 underline hover:text-purple-07"
                  >
                    Login
                  </button>
                </p>
              </div>
            </>
          )}
        </form>
      </div>
    </Layout>
  );
};

export default SignUp;
